import { useCallback, useEffect, useMemo, useState } from "react";
import { supabase } from "@/integrations/supabase/client";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Textarea } from "@/components/ui/textarea";
import { Input } from "@/components/ui/input";
import {
  Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle,
} from "@/components/ui/dialog";
import { toast } from "sonner";
import { Check, Eye, Loader2, RefreshCw, Undo2 } from "lucide-react";

type FormationDoc = {
  id: string;
  formation_id: string;
  instrument_slug: string;
  status: string;
  text_pdf_path: string | null;
  rasterized_pdf_path: string | null;
  page_count: number | null;
  edition: string | null;
  reviewer_id: string | null;
  reviewed_at: string | null;
  reject_reason: string | null;
};

type Formation = {
  id: string;
  current_document_edition: string | null;
  current_document_edition_status: string | null;
  legal_name: string | null;
  member_record_id: string;
  formation_documents: FormationDoc[];
};

const HUMAN: Record<string, string> = {
  in_review: "In review",
  approved: "Approved",
  rejected: "Sent back",
  queued: "Queued",
  rendering: "Rendering",
};

const INSTRUMENT_LABEL: Record<string, string> = {
  una_organization: "UNA Organization",
  ca_sub: "California Statement",
  una_constitution: "UNA Constitution",
  una_docs: "UNA Docs",
};

export default function FormationReview() {
  const [formations, setFormations] = useState<Formation[]>([]);
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState<string | null>(null);
  const [rejectTarget, setRejectTarget] = useState<FormationDoc | null>(null);
  const [rejectReason, setRejectReason] = useState("");
  const [memberNames, setMemberNames] = useState<Record<string, string>>({});

  const load = useCallback(async () => {
    setLoading(true);
    const { data, error } = await (supabase as any)
      .from("formations")
      .select("*, formation_documents(*)")
      .order("updated_at", { ascending: false })
      .limit(100);

    if (error) {
      toast.error("Could not load formations.");
    } else {
      setFormations((data ?? []) as Formation[]);
      const ids = (data ?? []).map((f: any) => f.member_record_id).filter(Boolean);
      if (ids.length) {
        const { data: recs } = await (supabase as any)
          .from("member_records")
          .select("id, first_name, last_name, legal_full_name")
          .in("id", ids);
        const map: Record<string, string> = {};
        for (const r of recs ?? []) {
          map[r.id] = String(r.legal_full_name || [r.first_name, r.last_name].filter(Boolean).join(" ")).trim();
        }
        setMemberNames(map);
      }
    }
    setLoading(false);
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  const queued = useMemo(() => {
    return formations.filter((f) =>
      f.formation_documents.some((d) => d.status === "in_review" || d.status === "rejected"),
    );
  }, [formations]);

  const callAction = async (doc: FormationDoc, action: string, reason?: string) => {
    setBusy(doc.id);
    try {
      const { data, error } = await supabase.functions.invoke("formation-review", {
        body: { action, formation_id: doc.formation_id, instrument_slug: doc.instrument_slug, reason },
      });
      if (error) throw error;
      const anyData = data as any;
      if (action === "preview") {
        if (anyData?.signed_url) {
          window.open(anyData.signed_url, "_blank", "noopener,noreferrer");
        } else {
          toast.error("There is no file to preview yet.");
        }
      } else {
        toast.success(anyData?.packet_approved ? "Packet approved and ready." : "Saved.");
        setRejectTarget(null);
        setRejectReason("");
        load();
      }
    } catch (e: any) {
      toast.error(e?.message ?? "That action did not go through.");
    } finally {
      setBusy(null);
    }
  };

  const statusColor = (s: string) => {
    if (s === "approved") return "bg-emerald-500/15 text-emerald-700";
    if (s === "rejected") return "bg-rose-500/15 text-rose-700";
    if (s === "in_review") return "bg-amber-500/15 text-amber-700";
    return "bg-muted text-muted-foreground";
  };

  return (
    <div className="space-y-6 p-6">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-semibold">Formation Review</h1>
          <p className="text-muted-foreground text-sm">
            Approve the generated packet documents before they move to the member's Record.
          </p>
        </div>
        <Button variant="outline" size="sm" onClick={load}>
          <RefreshCw className="h-4 w-4 mr-2" />
          Refresh
        </Button>
      </div>

      {loading ? (
        <div className="flex items-center gap-2 text-muted-foreground">
          <Loader2 className="h-4 w-4 animate-spin" />
          Loading packets
        </div>
      ) : queued.length === 0 ? (
        <Card>
          <CardContent className="pt-6 text-muted-foreground">
            Nothing is waiting for review. Documents appear here when the render service delivers them or a CA Sub is filled.
          </CardContent>
        </Card>
      ) : (
        queued.map((f) => (
          <Card key={f.id}>
            <CardHeader>
              <CardTitle className="text-lg">
                {memberNames[f.member_record_id] || "Member"} · {INSTRUMENT_LABEL[String(f.formation_documents[0]?.instrument_slug)] ?? "Formation packet"}
              </CardTitle>
              <CardDescription>
                Edition {f.current_document_edition || f.formation_documents[0]?.edition || "current"} ·{" "}
                <Badge variant="outline">{f.current_document_edition_status ?? "in_progress"}</Badge>
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-3">
              {f.formation_documents.map((d) => (
                <div key={d.id} className="flex items-center justify-between rounded-lg border p-3">
                  <div>
                    <div className="font-medium">{INSTRUMENT_LABEL[d.instrument_slug] ?? d.instrument_slug}</div>
                    <div className="text-xs text-muted-foreground">
                      {d.page_count != null ? `${d.page_count} pages` : "Page count pending"}
                      {d.reviewed_at ? ` · reviewed ${new Date(d.reviewed_at).toLocaleDateString()}` : ""}
                      {d.reject_reason ? ` · ${d.reject_reason}` : ""}
                    </div>
                  </div>
                  <div className="flex items-center gap-2">
                    <Badge className={statusColor(d.status)}>{HUMAN[d.status] ?? d.status}</Badge>
                    <Button
                      variant="outline"
                      size="sm"
                      disabled={busy === d.id || !d.text_pdf_path}
                      onClick={() => callAction(d, "preview")}
                    >
                      <Eye className="h-4 w-4 mr-1" />
                      Preview
                    </Button>
                    {d.status !== "approved" && (
                      <>
                        <Button variant="outline" size="sm" disabled={busy === d.id} onClick={() => callAction(d, "approve")}>
                          <Check className="h-4 w-4 mr-1" />
                          Approve
                        </Button>
                        <Button variant="ghost" size="sm" disabled={busy === d.id} onClick={() => setRejectTarget(d)}>
                          <Undo2 className="h-4 w-4 mr-1" />
                          Send back
                        </Button>
                      </>
                    )}
                  </div>
                </div>
              ))}
            </CardContent>
          </Card>
        ))
      )}

      <Dialog open={!!rejectTarget} onOpenChange={(o) => { if (!o) setRejectTarget(null); }}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Send this document back</DialogTitle>
            <DialogDescription>
              Tell the team what to correct. The document returns to the queue for another pass.
            </DialogDescription>
          </DialogHeader>
          <Textarea
            value={rejectReason}
            onChange={(e) => setRejectReason(e.target.value)}
            placeholder="What needs to change?"
            rows={3}
          />
          <DialogFooter>
            <Button variant="outline" onClick={() => setRejectTarget(null)}>Cancel</Button>
            <Button
              variant="destructive"
              disabled={!rejectReason.trim() || busy === rejectTarget?.id}
              onClick={() => rejectTarget && callAction(rejectTarget, "reject", rejectReason)}
            >
              {busy === rejectTarget?.id ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : null}
              Send back
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
